import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Crown, Star, Zap, Check, Pencil, X, HardDrive, FileText, Sparkles, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";

type Plan = {
  id: string;
  name: string;
  slug: string;
  tier: string;
  article_limit: number;
  disk_space_mb: number;
  ai_credits: number;
  monthly_price: number;
  is_active: boolean;
  features: string[];
};

const tierIcons: Record<string, React.ElementType> = { small: Zap, medium: Star, large: Crown };
const tierColors: Record<string, string> = {
  small: "border-border",
  medium: "border-primary/30 ring-1 ring-primary/10",
  large: "border-warning/30 ring-1 ring-warning/10",
};

async function fetchPlans(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from("subscription_plans")
    .select("*")
    .order("monthly_price", { ascending: true });
  if (error) throw error;
  return (data || []).map((p) => ({ ...p, features: Array.isArray(p.features) ? p.features as string[] : [] }));
}

export default function PlansPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Plan>>({});

  const { data: plans = [], isLoading } = useQuery({ queryKey: ["plans"], queryFn: fetchPlans });

  const updateMutation = useMutation({
    mutationFn: async (plan: Partial<Plan> & { id: string }) => {
const { id, features, tier, ...rest } = plan;
      const { error } = await supabase.from("subscription_plans").update(rest).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      toast.success("Plan updated");
      setEditingId(null);
    },
    onError: () => toast.error("Failed to update plan"),
  });

  const startEdit = (plan: Plan) => {
    setEditingId(plan.id);
    setForm({ article_limit: plan.article_limit, disk_space_mb: plan.disk_space_mb, ai_credits: plan.ai_credits, monthly_price: plan.monthly_price });
  };

  const saveEdit = (id: string) => {
    updateMutation.mutate({ id, ...form });
  };

  // Stats
  const totalRevenue = plans.reduce((s, p) => s + p.monthly_price, 0);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-6">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Plans & Billing</h1>
          <p className="text-xs text-muted-foreground">Manage subscription tiers and limits</p>
        </div>
      </header>

      <div className="p-6 space-y-8 max-w-6xl mx-auto animate-slide-in">
        {/* Overview stats */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard icon={DollarSign} label="Revenue / mo (base)" value={`$${totalRevenue.toFixed(2)}`} />
          <StatCard icon={FileText} label="Plans Active" value={plans.filter((p) => p.is_active).length} />
          <StatCard icon={Sparkles} label="Total AI Credits Pool" value={plans.reduce((s, p) => s + p.ai_credits, 0)} />
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="animate-pulse h-80" />
              ))
            : plans.map((plan) => {
                const Icon = tierIcons[plan.tier] || Zap;
                const isEditing = editingId === plan.id;

                return (
                  <Card key={plan.id} className={cn("relative overflow-hidden transition-all", tierColors[plan.tier])}>
                    {plan.tier === "large" && (
                      <div className="absolute top-0 right-0 rounded-bl-lg bg-warning/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-warning">
                        Popular
                      </div>
                    )}
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "flex h-10 w-10 items-center justify-center rounded-lg",
                          plan.tier === "large" ? "bg-warning/15" : plan.tier === "medium" ? "bg-primary/15" : "bg-accent"
                        )}>
                          <Icon className={cn("h-5 w-5", plan.tier === "large" ? "text-warning" : plan.tier === "medium" ? "text-primary" : "text-muted-foreground")} />
                        </div>
                        <div>
                          <CardTitle className="text-base">{plan.name}</CardTitle>
                          <CardDescription className="text-xs capitalize">{plan.tier} tier</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Price */}
                      <div className="flex items-baseline gap-1">
                        {isEditing ? (
                          <Input
                            type="number"
                            value={form.monthly_price ?? ""}
                            onChange={(e) => setForm({ ...form, monthly_price: parseFloat(e.target.value) || 0 })}
                            className="w-24 h-8 text-lg font-bold"
                          />
                        ) : (
                          <span className="text-3xl font-bold text-foreground">${plan.monthly_price}</span>
                        )}
                        <span className="text-sm text-muted-foreground">/ mo</span>
                      </div>

                      {/* Limits */}
                      <div className="space-y-3">
                        <LimitRow icon={FileText} label="Articles" editing={isEditing} value={isEditing ? form.article_limit! : plan.article_limit} onChange={(v) => setForm({ ...form, article_limit: v })} />
                        <LimitRow icon={HardDrive} label="Disk Space (MB)" editing={isEditing} value={isEditing ? form.disk_space_mb! : plan.disk_space_mb} onChange={(v) => setForm({ ...form, disk_space_mb: v })} />
                        <LimitRow icon={Sparkles} label="AI Credits" editing={isEditing} value={isEditing ? form.ai_credits! : plan.ai_credits} onChange={(v) => setForm({ ...form, ai_credits: v })} />
                      </div>

                      {/* Features */}
                      <div className="space-y-1.5 pt-2 border-t border-border">
                        {plan.features.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Check className="h-3 w-3 text-success" />
                            {f}
                          </div>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="pt-2">
                        {isEditing ? (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => saveEdit(plan.id)} disabled={updateMutation.isPending}>
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="w-full" onClick={() => startEdit(plan)}>
                            <Pencil className="h-3 w-3 mr-1" /> Edit Limits
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
        </div>
      </div>
    </div>
  );
}

function LimitRow({ icon: Icon, label, value, editing, onChange }: { icon: React.ElementType; label: string; value: number; editing: boolean; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      {editing ? (
        <Input type="number" value={value} onChange={(e) => onChange(parseInt(e.target.value) || 0)} className="w-20 h-7 text-right text-sm" />
      ) : (
        <span className="text-sm font-semibold text-foreground">{value.toLocaleString()}</span>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xl font-bold text-foreground">{value}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
